// src/components/MediaDetailWithSidebar/MediaDetailWithSidebar.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  useMediaQuery,
  // DialogTitle,
} from "@mui/material";
import { FavoriteBorder, Favorite, PlayCircleOutline, Close, ChevronLeft, ChevronRight } from "@mui/icons-material";
import ContentCard from "../ContentCard/ContentCard";
import { isValidMediaUrl, setEntity, getEntity } from "../../common/CommonFunctions";
import { AppTheme } from "../../theme/App.theme";
import MUIButton from "../MUIButton/MUIButton";
import { Icons } from "../../config/MuiIcons.config";
import { useNavigate } from "react-router-dom";
import chapterManagementService from "../../services/ChapterManagementService";
import favoritesManagementService from "../../services/FavoritesManagementService";
import CardGrid from "../CardGrid/CardGrid";
import dayjs from "dayjs";
import { AppGlobalContext } from "../../contexts/AppContext.context";
import { useScrollManagement } from "../../contexts/ScrollManagement.context";
import { useSnackbar } from "../../contexts/Snackbar.context";
import { SNACKBAR_VARIANT, APP_MESSAGE } from "../../config/Messages.config";
import AutoCarousel from "../AutoCarousel/AutoCarousel";
import "./MediaDetailWithSidebar.style.css";
// import ThumbnailImage from "../ThumbnailImage/ThumbnailImage";

// Demo/fallback chapter sources if API doesn't return content yet
const DEMO_CHAPTER_CONTENT = [
  { type: "media", srcLink: "https://heyzine.com/flip-book/0b1cb5d3d0.html%22" },
  { type: "comic", srcLink: "https://heyzine.com/flip-book/fdee67ebf2.html%22" },
];

const toVimeoEmbed = (url) => {
  try {
    const u = new URL(url);
    // Handles vimeo.com/12345, player.vimeo.com/video/12345, and extra path parts
    const digits = u.pathname.match(/\/(?:video\/)?(\d+)/);
    if (!digits || !digits[1]) return null;
    return `https://player.vimeo.com/video/${digits[1]}`;
  } catch {
    return null;
  }
};

const IframeRenderer = ({
  url,
  fallbackImage,
  className,
  thumbImages = [],
}) => {
  const [loaded, setLoaded] = useState(false);
  const [fallbackToThumbs, setFallbackToThumbs] = useState(false);
  const timeoutRef = useRef(null);

  const isVimeo = useMemo(() => !!url && /vimeo\.com/i.test(url), [url]);
  const isHeyzine = useMemo(() => !!url && /heyzine\.com/i.test(url), [url]);

  const embedUrl = useMemo(() => {
    if (!url) return "";
    if (isVimeo) return toVimeoEmbed(url) || "";
    if (isHeyzine) return url;
    return url;
  }, [url, isVimeo, isHeyzine]);

  const images = useMemo(
    () => (thumbImages?.length ? thumbImages : [fallbackImage]).filter(Boolean),
    [thumbImages, fallbackImage]
  );

  // Reset and start a safety timeout; if iframe never loads, fallback to thumbnail(s)
  useEffect(() => {
    setLoaded(false);
    setFallbackToThumbs(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (embedUrl) {
      timeoutRef.current = setTimeout(() => setFallbackToThumbs(true), 6000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [embedUrl]);

  const renderThumbs = images.length > 0 && (
    <Box className={`mds-iframe-wrapper thumb ${className || ""}`}>
      {images.length > 1 ? (
        <div className="mds-thumb-carousel">
          <AutoCarousel
            items={images.map((src, i) => ({ id: i, imageUrl: src }))}
            height={"100%"}
            showDots={true}
            useGradientFirst={false}
          />
        </div>
      ) : (
        <img
          src={images[0]}
          alt="media"
          className="mds-iframe-fallback-image"
          onLoad={() => setLoaded(true)}
        />
      )}
    </Box>
  );

  if (!embedUrl || !isValidMediaUrl(embedUrl) || fallbackToThumbs) {
    return (
      renderThumbs || (
        <Box className={`mds-iframe-empty ${className || ""}`}>
          <Typography variant="subtitle1">
            Unsupported or missing media URL
          </Typography>
        </Box>
      )
    );
  }

  return (
    <Box
      className={`mds-iframe-wrapper ${isVimeo ? "vimeo" : ""} ${
        isHeyzine ? "heyzine" : ""
      } ${className || ""}`}
    >
      {!loaded && (
        <Box className="mds-iframe-loader">
          <CircularProgress size={28} />
        </Box>
      )}
      <iframe
        title="media"
        src={embedUrl}
        className="mds-iframe"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        onLoad={() => {
          setLoaded(true);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }}
        onError={() => setFallbackToThumbs(true)}
      />
    </Box>
  );
};

// Single sidebar list item (thumbnail, title/subtitle, actions)
const SidebarListItem = ({
  image,
  title,
  subtitle,
  active,
  showPlayButton,
  showLearnButton,
  showQuizButton,
  learnButtonText = "Learn",
  quizButtonText = "Take Quiz",
  onClick,
  onPlayClick,
  onLearnClick,
  onQuizClick,
  variant = "default", // "default" | "ebook"
}) => {
  const handlePlay = (e) => {
    e.stopPropagation();
    onPlayClick?.();
  };
  const handleLearn = (e) => {
    e.stopPropagation();
    onLearnClick?.();
  };
  const handleQuiz = (e) => {
    e.stopPropagation();
    onQuizClick?.();
  };

  // Ebook-like card layout
  if (variant === "ebook") {
    return (
      <Box
        className={`mds-sideitem ebook ${active ? "is-active" : ""}`}
        onClick={onClick}
      >
        <div className="mds-sideitem-thumb">
          {image ? (
            <img src={image} alt={title || "thumbnail"} />
          ) : (
            <div className="mds-thumb-fallback" />
          )}
        </div>

        <div className="mds-sideitem-text">
          {title ? <div className="mds-sideitem-title">{title}</div> : null}
          {subtitle ? (
            <div className="mds-sideitem-subtitle">{subtitle}</div>
          ) : null}
        </div>
      </Box>
    );
  }

  // Chapter-like list layout
  if (variant === "chapter") {
    return (
      <Box
        className={`mds-sideitem chapter ${active ? "is-active" : ""}`}
        onClick={onClick}
      >
        <div className="mds-sideitem-thumb">
          {image ? (
            <img src={image} alt={title || "thumbnail"} />
          ) : (
            <div className="mds-thumb-fallback" />
          )}
        </div>

        <div className="mds-sideitem-text">
          {title ? <div className="mds-sideitem-title">{title}</div> : null}

          <div className="mds-chapter-actions">
            {showLearnButton && (
              <MUIButton primary size={"small"} onClick={handleLearn}>
                {learnButtonText}
              </MUIButton>
            )}
            {showQuizButton && (
              <MUIButton
                primary
                variant={"outlined"}
                size={"small"}
                onClick={handleQuiz}
              >
                {quizButtonText}
              </MUIButton>
            )}
          </div>

          {subtitle ? (
            <div className="mds-sideitem-subtitle">{subtitle}</div>
          ) : null}
        </div>
      </Box>
    );
  }

  return (
    <Box
      className={`mds-sideitem ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      <div className="mds-sideitem-thumb">
        {image ? (
          <img src={image} alt={title || "thumbnail"} />
        ) : (
          <div className="mds-thumb-fallback" />
        )}
      </div>

      <div className="mds-sideitem-text">
        {title ? <div className="mds-sideitem-title">{title}</div> : null}
        {subtitle ? (
          <div className="mds-sideitem-subtitle">{subtitle}</div>
        ) : null}
      </div>

      <div className="mds-sideitem-actions">
        {showPlayButton && (
          <button
            type="button"
            className="mds-play-btn"
            onClick={handlePlay}
            aria-label="Play"
          >
            <PlayCircleOutline className="mds-play-icon" />
          </button>
        )}
        {showLearnButton && (
          <MUIButton
            primary
            size={"small"}
            onClick={handleLearn}
            children={learnButtonText}
          />
          // <button type="button" className="mds-action-btn" onClick={handleLearn}>{learnButtonText}</button>
        )}
        {showQuizButton && (
          <MUIButton
            primary
            variant={"outlined"}
            size={"small"}
            onClick={handleQuiz}
            children={quizButtonText}
          />
          // <button type="button" className="mds-action-btn outlined" onClick={handleQuiz}>{quizButtonText}</button>
        )}
      </div>
    </Box>
  );
};

const MediaDetailWithSidebar = ({
  // Modes
  mode = "list", // "list" | "detail"

  // Detail mode inputs (no sidebar/list fetch)
  detailItem = null, // pass pre-fetched entity (Podcast/Topic/Bhakti-Sangeet/etc.)
  detailItemId = null, // or an ID...
  fetchItem = null, // ...and a fetcher: async (id) => item or {data: item}

  // Data fetching
  fetchItems, // async ({page, limit, cursor, ...fetchParams}) => { items, nextCursor, hasMore }
  fetchParams = {},
  pageSize = 20,

  // Item mappers
  getId,
  getTitle,
  getSubtitle,
  getThumbnail,
  getIframeUrl,
  getDescription,

  // Behavior & UI
  sidebarItemLayout = "horizontal", // kept for future; not used by custom cell
  onCardClick,
  onPlayClick,
  onLearnClick,
  onQuizClick,
  className = "",
  contentType,
  isFavoritesMode = false, // New prop to indicate if this is used in favorites section
}) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [activeChapterType, setActiveChapterType] = useState("media");
  const [isFavoriting, setIsFavoriting] = useState(false); // Track if any favorite operation is in progress
  const [tempRelatedEbook, setTempRelatedEbook] = useState(null); // Track temporary related e-book in favorites mode

  const isDetailMode = mode === "detail";
  const isMobile = useMediaQuery("(max-width:900px)");
  const { SetAppState, authUserDetails } = React.useContext(AppGlobalContext);
  const { smartScrollToElement } = useScrollManagement();
  const { showSnackbar } = useSnackbar();
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const showSidebar = !isDetailMode && !(isMobile && mobileShowDetail);
  const navigate = useNavigate();

  const sentinelRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);
  const executeLoadRef = useRef(null);
  const sidebarListRef = useRef(null);
  const mainRef = useRef(null);

  // ADDED: track iframe navigations to skip joint history on back
  const iframeNavCountRef = useRef(0);
  const prevIframeUrlRef = useRef("__INIT__");

  // Smart back: keep calling back() until the app route changes (ignores iframe history)
  const handleBackNav = useCallback(() => {
    const baseline =
      window.location.pathname + window.location.search + window.location.hash;
    let steps = 0;
    const maxSteps = 50;

    const tick = () => {
      const now =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (now !== baseline) return; // route changed → stop

      if (steps >= maxSteps) {
        // Fallback: one normal back if something unusual happens
        window.history.back();
        return;
      }

      steps += 1;
      window.history.back();
      setTimeout(tick, 50);
    };

    tick();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helpers that resolve either string key or function, with safe fallbacks
  const mapGetId = useCallback(
    (i) =>
      typeof getId === "string"
        ? i?.[getId]
        : getId
        ? getId(i)
        : i?.id ?? i?._id ?? i?.slug,
    [getId]
  );
  const mapGetTitle = useCallback(
    (i) =>
      typeof getTitle === "string"
        ? i?.[getTitle]
        : getTitle
        ? getTitle(i)
        : i?.topicName || i?.title || "",
    [getTitle]
  );
  const mapGetSubtitle = useCallback(
    (i) => {
      if (typeof getSubtitle === "string") return i?.[getSubtitle];
      if (getSubtitle) return getSubtitle(i);
  
      // Normalize updatedAt
      if (i?.updatedAt) {
        if (typeof i.updatedAt === "string") {
          return dayjs(i.updatedAt).format("D MMM, YYYY");
        } else if (typeof i.updatedAt === "object" && i.updatedAt._seconds) {
          return dayjs(i.updatedAt._seconds * 1000).format("D MMM, YYYY");
        }
      }
  
      return i?.subtitle || "";
    },
    [getSubtitle]
  );
  const mapGetThumbnail = useCallback(
    (i) => {
      if (typeof getThumbnail === "string") return i?.[getThumbnail];
      if (getThumbnail) return getThumbnail(i);
      return (
        i?.thumbnailImage || i?.thumbnail || i?.image || i?.coverImage || ""
      );
    },
    [getThumbnail]
  );
  const mapGetIframeUrl = useCallback(
    (i) => {
      if (typeof getIframeUrl === "string") return i?.[getIframeUrl];
      if (getIframeUrl) return getIframeUrl(i);
      return i?.srcLink || i?.iframeUrl || i?.url || "";
    },
    [getIframeUrl]
  );
  const mapGetDescription = useCallback(
    (i) =>
      typeof getDescription === "string"
        ? i?.[getDescription]
        : getDescription
        ? getDescription(i)
        : i?.description || i?.summary || i?.content || "",
    [getDescription]
  );

  // Try to extract chapter content sources from common shapes
  const extractChapterContentSources = useCallback((chapter) => {
    if (!chapter) return [];
    const arrays = [
      chapter?.contents,
      chapter?.content,
      chapter?.items,
      chapter?.chaptersContent,
    ].filter(Array.isArray);

    if (arrays.length > 0) {
      const flat = arrays[0];
      return flat
        .map((c) => ({
          type: (c?.type || c?.contentType || c?.category || "").toLowerCase(),
          srcLink: c?.srcLink || c?.url || c?.iframeUrl || "",
        }))
        .filter((c) => c.srcLink);
    }

    // Single field fallbacks
    const candidates = [
      { type: "media", srcLink: chapter?.mediaLink || chapter?.srcLink || chapter?.url },
      { type: "comic", srcLink: chapter?.comicLink },
    ].filter((c) => c.srcLink);

    return candidates;
  }, []);

  // Gallery images mapper (tries common keys and shapes)
  const mapGetImages = useCallback((i) => {
    if (!i) return [];
    const arr =
      i.images ||
      i.gallery ||
      i.photos ||
      i.media ||
      (typeof i.imageList === "string"
        ? i.imageList
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : i.imageList) ||
      [];
    const toUrl = (x) =>
      typeof x === "string"
        ? x
        : x?.url ||
          x?.src ||
          x?.image ||
          x?.thumbnailImage ||
          x?.thumbnail ||
          "";
    return (Array.isArray(arr) ? arr : []).map(toUrl).filter(Boolean);
  }, []);

  // Favorite handling functions
  const getCurrentUserId = useCallback(() => {
    return authUserDetails?.uid || authUserDetails?.id || null;
  }, [authUserDetails]);

  const isContentFavorited = useCallback((item) => {
    if (!item) return false;
    // Check for isFavorite flag in the content item
    return Boolean(item.isFavorite);
  }, []);

  // Refetch function for favorites mode
  const refetchData = useCallback(() => {
    if (!isFavoritesMode || !executeLoadRef.current) return;
    
    // Reset pagination and clear items
    setPage(1);
    setHasMore(true);
    setItems([]);
    
    // Force refetch by calling the load function directly
    // This ensures it works on both desktop and mobile
    const loadPage = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (isMountedRef.current) setLoading(true);
      try {
        const { page: _p, limit: _l, ...restParams } = fetchParams || {};
        
        // Add type parameter if in favorites mode
        const apiParams = {
          page: 1, // Always start from page 1 for refetch
          limit: pageSize,
          ...restParams,
        };
        
        if (isFavoritesMode && contentType) {
          apiParams.type = contentType;
        }
        
        const res = await fetchItems.getAll(apiParams);

        if (!isMountedRef.current) return;

        const newItems = isFavoritesMode ? (res?.data[contentType]?.map(d => ({
          ...d.content,
          isFavorite: true
        })) || []) : res?.data || [];
        
        setItems(newItems); // Replace items completely for refetch

        const nextPageExists = Boolean(
          res?.pagination?.hasNextPage ?? newItems.length === pageSize
        );
        setHasMore(nextPageExists);
        setPage(2); // Set to 2 since we just loaded page 1
      } catch (err) {
        console.error("MediaDetailWithSidebar: refetch failed", err);
      } finally {
        if (isMountedRef.current) setLoading(false);
        isFetchingRef.current = false;
      }
    };
    
    loadPage();
  }, [isFavoritesMode, fetchItems, pageSize, fetchParams, contentType]);

  const handleFavoriteToggle = useCallback(async (item) => {
    if (!item || !contentType || isFavoriting) return;
    
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn("User not authenticated, cannot toggle favorite");
      return;
    }

    const contentId = mapGetId(item);
    const isCurrentlyFavorited = isContentFavorited(item);
    
    // Determine the correct content type for the API call
    // If we're viewing a chapter, use "chapter" as the type
    const effectiveContentType = selectedChapter ? "chapter" : contentType;
    
    // Set flag to prevent multiple simultaneous operations
    setIsFavoriting(true);

    try {
      if (isCurrentlyFavorited) {
        // Remove from favorites
        await favoritesManagementService.deleteById(`${userId}_${contentId}`);
        showSnackbar(APP_MESSAGE.FAVORITES.REMOVE.SUCCESS, SNACKBAR_VARIANT.SUCCESS);
        
        // If in favorites mode, refetch data to get updated list
        if (isFavoritesMode) {
          // Clear selected item since it's being removed from favorites
          setSelectedId(null);
          // Clear selectedChapter if it's the one being removed
          if (selectedChapter && mapGetId(selectedChapter) === contentId) {
            setSelectedChapter(null);
          }
          // Update chapters array if the removed item is a chapter
          setChapters(prev => prev.map(chapter => 
            mapGetId(chapter) === contentId ? { ...chapter, isFavorite: false } : chapter
          ));
          // On mobile/tablet, return to sidebar view after removing from favorites
          if (isMobile) {
            setMobileShowDetail(false);
          }
          // Use a small delay to ensure state updates complete before refetching
          setTimeout(() => {
            refetchData();
          }, 100);
        } else {
          // In normal mode, just update the item's isFavorite flag
          const updatedItem = { ...item, isFavorite: false };
          setItems(prev => prev.map(i => mapGetId(i) === contentId ? updatedItem : i));
          
          // If we're viewing a chapter, also update the selectedChapter state
          if (selectedChapter && mapGetId(selectedChapter) === contentId) {
            setSelectedChapter(updatedItem);
          }
          
          // Update the chapter in the chapters array as well
          setChapters(prev => prev.map(chapter => 
            mapGetId(chapter) === contentId ? updatedItem : chapter
          ));
          
          // Update tempRelatedEbook if it's the one being updated
          if (tempRelatedEbook && mapGetId(tempRelatedEbook) === contentId) {
            setTempRelatedEbook(updatedItem);
          }
        }
      } else {
        // Add to favorites
        const favoriteData = {
          contentId,
          userId,
          type: effectiveContentType,
          createdBy: userId
        };
        await favoritesManagementService.createOne(favoriteData);
        showSnackbar(APP_MESSAGE.FAVORITES.ADD.SUCCESS, SNACKBAR_VARIANT.SUCCESS);
        
        // If in favorites mode, refetch data to get updated list
        if (isFavoritesMode) {
          // Update selectedChapter if it's the one being added to favorites
          if (selectedChapter && mapGetId(selectedChapter) === contentId) {
            setSelectedChapter({ ...selectedChapter, isFavorite: true });
          }
          // Update tempRelatedEbook if it's the one being added to favorites
          if (tempRelatedEbook && mapGetId(tempRelatedEbook) === contentId) {
            setTempRelatedEbook({ ...tempRelatedEbook, isFavorite: true });
          }
          // Update chapters array if the added item is a chapter
          setChapters(prev => prev.map(chapter => 
            mapGetId(chapter) === contentId ? { ...chapter, isFavorite: true } : chapter
          ));
          // Use a small delay to ensure state updates complete before refetching
          setTimeout(() => {
            refetchData();
          }, 100);
        } else {
          // Update the item's isFavorite flag immediately
          const updatedItem = { ...item, isFavorite: true };
          setItems(prev => prev.map(i => mapGetId(i) === contentId ? updatedItem : i));
          
          // If we're viewing a chapter, also update the selectedChapter state
          if (selectedChapter && mapGetId(selectedChapter) === contentId) {
            setSelectedChapter(updatedItem);
          }
          
          // Update the chapter in the chapters array as well
          setChapters(prev => prev.map(chapter => 
            mapGetId(chapter) === contentId ? updatedItem : chapter
          ));
          
          // Update tempRelatedEbook if it's the one being updated
          if (tempRelatedEbook && mapGetId(tempRelatedEbook) === contentId) {
            setTempRelatedEbook(updatedItem);
          }
        }
      }
    } catch (error) {
      // Show error message
      if (isCurrentlyFavorited) {
        showSnackbar(APP_MESSAGE.FAVORITES.REMOVE.FAILED, SNACKBAR_VARIANT.ERROR);
      } else {
        showSnackbar(APP_MESSAGE.FAVORITES.ADD.FAILED, SNACKBAR_VARIANT.ERROR);
      }
      
      console.error("Failed to toggle favorite:", error);
    } finally {
      setIsFavoriting(false);
    }
  }, [contentType, isFavoriting, getCurrentUserId, isContentFavorited, mapGetId, selectedId, selectedChapter, isFavoritesMode, refetchData, isMobile]);

  const selectedItem = useMemo(() => {
    if (!items?.length && !tempRelatedEbook) return null;
    if (selectedId == null) return null; // no auto-select; wait for explicit choice
    
    // In favorites mode, if we have a temp related e-book and it matches selectedId, return it
    if (isFavoritesMode && tempRelatedEbook && mapGetId(tempRelatedEbook) === selectedId) {
      return tempRelatedEbook;
    }
    
    return items.find((i) => mapGetId(i) === selectedId) || null;
  }, [items, selectedId, mapGetId, tempRelatedEbook, isFavoritesMode]);

  // Compute currently focused detail (topic or chapter)
  const currentDetail = selectedChapter || selectedItem;
  const effectiveChapter = useMemo(
    () => selectedChapter || (contentType === "chapter" ? selectedItem : null),
    [selectedChapter, contentType, selectedItem]
  );

  // Chapter sources and active URL
  const chapterSources = useMemo(() => {
    const extracted = extractChapterContentSources(effectiveChapter);
    return extracted?.length ? extracted : effectiveChapter ? DEMO_CHAPTER_CONTENT : [];
  }, [effectiveChapter, extractChapterContentSources]);

  const activeChapterUrl = useMemo(() => {
    if (!effectiveChapter) return "";
    const lower = (activeChapterType || "").toLowerCase();
    const found = chapterSources.find((s) => (s.type || "").toLowerCase() === lower) || chapterSources[0];
    return found?.srcLink || "";
  }, [effectiveChapter, activeChapterType, chapterSources]);

  // Determine default tab selection based on available types
  const computeDefaultType = useCallback((sources) => {
    if (!sources || sources.length === 0) return "media";
    const types = sources
      .map((s) => (s?.type || "").toLowerCase())
      .filter(Boolean);
    if (types.includes("media")) return "media"; // prefer media when both
    if (types.includes("comic")) return "comic";
    return types[0];
  }, []);

  useEffect(() => {
    if (!effectiveChapter) {
      setActiveChapterType("media");
      return;
    }
    const def = computeDefaultType(chapterSources);
    setActiveChapterType(def);
  }, [effectiveChapter, chapterSources, computeDefaultType]);

  // Fetch chapters when educational topic is selected
  useEffect(() => {
    const shouldFetchChapters =
      contentType === "topic" && Boolean(selectedItem);

    if (!shouldFetchChapters) return;

    let active = true;
    setChapters([]);
    (async () => {
      try {
        setChaptersLoading(true);
        const res = await chapterManagementService.getAllByTopic(
          mapGetId(selectedItem),
          {
            page: 1,
            limit: 4,
            isActive: true,
            status: "published",
            laguage: "English",
          }
        );
        if (!active) return;
        const data = res?.data ?? res ?? [];
        setChapters(Array.isArray(data) ? data : []);
      } catch (err) {
        if (active) setChapters([]);
        console.error("Failed to fetch chapters", err);
      } finally {
        if (active) setChaptersLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // Only depend on contentType and selectedId, not the entire selectedItem object
    // This prevents refetching when isFavorite flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, selectedId]);

  // Description expand/collapse
  const [descExpanded, setDescExpanded] = useState(false);

  const fullDescription = useMemo(
    () => (currentDetail ? mapGetDescription(currentDetail) || "" : ""),
    [currentDetail, mapGetDescription]
  );

  const isLongDescription =
    typeof fullDescription === "string" && fullDescription.length > 200;

  const displayDescription =
    !descExpanded && isLongDescription
      ? `${fullDescription.slice(0, 200)}...`
      : fullDescription;

  useEffect(() => {
    setDescExpanded(false);
  }, [currentDetail]);

  // Reset detail panel when leaving mobile viewport
  useEffect(() => {
    if (!isMobile) setMobileShowDetail(false);
  }, [isMobile]);

  // Smart scrolling for all devices when content changes
  // This ensures proper scrolling behavior across mobile, tablet, and desktop
  useEffect(() => {
    // Trigger scrolling when:
    // 1. Mobile detail view is shown
    // 2. Desktop/tablet item is selected
    // 3. Selected item changes
    // 4. Detail mode with selected item
    const shouldScroll =
      mobileShowDetail ||
      (!isMobile && selectedItem) ||
      (isDetailMode && selectedItem);

    if (shouldScroll) {
      // Use a timeout to ensure the DOM has updated
      setTimeout(() => {
        const el = mainRef.current;
        if (el) {
          // Use different offsets based on screen size
          const offset = isMobile ? 20 : 10; // Less offset on larger screens
          // Use the smart scroll function that handles parent scrolling
          smartScrollToElement(el, offset);
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 200);
    }
  }, [
    mobileShowDetail,
    selectedItem,
    isMobile,
    isDetailMode,
    smartScrollToElement,
  ]);

  // ADDED: count iframe navigations to compute how many history steps to skip
  useEffect(() => {
    const url = currentDetail
      ? (effectiveChapter ? activeChapterUrl : mapGetIframeUrl(selectedItem))
      : null;
    if (url && prevIframeUrlRef.current && url !== prevIframeUrlRef.current) {
      iframeNavCountRef.current += 1;
    }
    prevIframeUrlRef.current = url || "__INIT__";
  }, [currentDetail, effectiveChapter, activeChapterUrl, selectedItem, mapGetIframeUrl]);

  // Register/unregister subheader back handler
  useEffect(() => {
    if (isDetailMode) {
      // detail routes: go back to last visited app route (skip iframe history)
      SetAppState({ subHeaderBackHandler: handleBackNav });
      return;
    }

    const backToList = () => {
      setMobileShowDetail(false);
      setSelectedId(null);
    };

    if (selectedChapter) {
      // while viewing a chapter, back should return to topic view
      SetAppState({ subHeaderBackHandler: () => setSelectedChapter(null) });
    } else if (isMobile && mobileShowDetail) {
      SetAppState({ subHeaderBackHandler: backToList });
    } else {
      // list routes: go back to previous app route (skip iframe history)
      SetAppState({ subHeaderBackHandler: handleBackNav });
    }
    return () => SetAppState({ subHeaderBackHandler: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, mobileShowDetail, isDetailMode, handleBackNav]);

  // DETAIL MODE: fetch only one item (if not pre-provided), do not set up list fetching or sidebar
  useEffect(() => {
    if (!isDetailMode) return;
    let active = true;

    (async () => {
      if (detailItem) {
        setItems([detailItem]);
        setSelectedId(mapGetId(detailItem));
        return;
      }

      if (!fetchItem || detailItemId == null) {
        setItems([]);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchItem(detailItemId);
        const one =
          res && typeof res === "object" && "data" in res ? res.data : res;
        if (!active) return;
        if (one) {
          setItems([one]);
          setSelectedId(mapGetId(one));
        } else {
          setItems([]);
        }
      } catch (err) {
        if (active) setItems([]);
        console.error("MediaDetailWithSidebar: single fetch failed", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isDetailMode, detailItem, detailItemId, fetchItem, mapGetId]);

  useEffect(() => {
    if (!showSidebar || !fetchItems?.getAll) return;

    executeLoadRef.current = async function loadPage() {
      if (isFetchingRef.current || !hasMore) return;
      isFetchingRef.current = true;

      if (isMountedRef.current) setLoading(true);
      try {
        const { page: _p, limit: _l, ...restParams } = fetchParams || {};
        
        // Add type parameter if in favorites mode
        const apiParams = {
          page,
          limit: pageSize,
          ...restParams,
        };
        
        if (isFavoritesMode && contentType) {
          apiParams.type = contentType;
        }
        
        const res = await fetchItems.getAll(apiParams);

        if (!isMountedRef.current) return;

        const newItems = isFavoritesMode ? (res?.data[contentType]?.map(d => ({
          ...d.content,
          isFavorite: true
        })) || []) : res?.data || [];

        setItems((prev) => {
          if (page === 1) {
            return newItems;
          } else {
            const map = new Map(prev.map((i) => [mapGetId(i), i]));
            newItems.forEach((i) => map.set(mapGetId(i), i));
            return Array.from(map.values());
          }
        });

        const nextPageExists = Boolean(
          res?.pagination?.hasNextPage ?? newItems.length === pageSize
        );
        setHasMore(nextPageExists);
        setPage((p) => p + 1);
      } catch (err) {
        console.error("MediaDetailWithSidebar: fetch failed", err);
      } finally {
        if (isMountedRef.current) setLoading(false);
        isFetchingRef.current = false;
      }
    };
  }, [showSidebar, hasMore, fetchItems, page, pageSize, fetchParams, mapGetId, isFavoritesMode, contentType]);

  useEffect(() => {
    // initial load (list mode only)
    if (!showSidebar) return;
    executeLoadRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);

  useEffect(() => {
    if (!showSidebar || !sentinelRef.current) return;
    const el = sentinelRef.current;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) executeLoadRef.current?.();
        });
      },
      { root: null, rootMargin: "0px 0px 300px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);

  const handleItemClick = (item) => {
    const id = mapGetId(item);
    // When switching topics while a chapter is open, return to topic view
    setSelectedChapter(null);
    // Clear temporary related e-book when clicking on a regular item
    setTempRelatedEbook(null);
    setSelectedId(id);
    onCardClick?.(item);
    if (isMobile && !isDetailMode) {
      setMobileShowDetail(true);
    }
  };

  const handleBackToTopic = useCallback(() => {
    setSelectedChapter(null);
    setActiveChapterType("media");
    // Clear temporary related e-book when going back to topic
    setTempRelatedEbook(null);
  }, []);

  // Navigate to Quiz page with metadata in state and session
  const handleGoToQuiz = useCallback((entity) => {
    // Resolve the chapter the quiz pertains to
    const candidateIsChapter = Boolean(entity?.type === "chapter" || entity?.chapterId);
    const chapter = candidateIsChapter
      ? entity
      : selectedChapter || (contentType === "chapter" ? selectedItem : null);

    if (!chapter) {
      console.warn("Quiz: Chapter not resolved; aborting navigation");
      return;
    }

    // Resolve topic context robustly
    let topicId = null;
    let topicTitle = "";

    if (contentType === "topic") {
      // Sidebar item may be the topic itself
      const t = entity || selectedItem;
      topicId = t ? mapGetId(t) : null;
      topicTitle = t ? mapGetTitle(t) : "";
    } else {
      // When viewing chapters, get topic from chapter or stored meta
      const meta = getEntity("CHAPTERS_META") || getEntity("QUIZ_META") || {};
      topicId = entity?.topicId || selectedItem?.topicId || selectedChapter?.topicId || meta?.topicId || null;
      topicTitle = meta?.topicTitle || "";
    }

    const payload = {
      topicId: topicId ?? undefined,
      topicTitle: topicTitle || "",
      chapterId: mapGetId(chapter),
      chapterTitle: mapGetTitle(chapter),
      chapterThumbnail: mapGetThumbnail(chapter),
    };

    try { setEntity(payload, "QUIZ_META"); } catch (_) {}
    navigate("/quiz", { state: payload });
  }, [navigate, selectedItem, selectedChapter, contentType, mapGetId, mapGetTitle, mapGetThumbnail]);

  // Handle related e-book clicks - add to items array if not already present
  const handleRelatedEbookClick = (item) => {
    const id = mapGetId(item);

    // Check if item already exists in items array
    const existingItem = items.find((i) => mapGetId(i) === id);

    if (!existingItem) {
      // In favorites mode, don't add to items array, just show details
      if (isFavoritesMode) {
        // Create a temporary detail view without adding to items array
        setTempRelatedEbook(item);
        setSelectedId(id);
        onCardClick?.(item);
        if (isMobile && !isDetailMode) {
          setMobileShowDetail(true);
        }
      } else {
        // Add the related e-book to the items array so it can be selected
        setItems((prev) => {
          const map = new Map(prev.map((i) => [mapGetId(i), i]));
          map.set(id, item);
          return Array.from(map.values());
        });

        // Set the selected ID after adding to items array
        setSelectedId(id);
        onCardClick?.(item);
        if (isMobile && !isDetailMode) {
          setMobileShowDetail(true);
        }
      }
    } else {
      // Item already exists, handle click normally
      handleItemClick(item);
    }
  };

  // Function to see all chapter
  const handleSeeAllChapters = (payload) => {
    try {
      setEntity(payload, "CHAPTERS_META");
    } catch (_) {}
    navigate(
      `/educational-chapters?topicId=${encodeURIComponent(
        payload.topicId
      )}`,
      { state: payload }
    );
  }

  // Lightbox for images
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const galleryImages = useMemo(
    () => (currentDetail ? mapGetImages(currentDetail) : []),
    [currentDetail, mapGetImages]
  );

  const handleShare = async () => {
    try {
      const shareData = {
        title: "Hindus R Us - " + mapGetTitle(currentDetail || {}),
        text: "Explore a vast collection of Hindu scriptures, teachings, and devotional content.",
        url: window.location.href,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch (_) {}
  };

  // Render function for ContentCard
  const renderContentCard = (card, index, onItemClick, layout, isSelected) => (
    <ContentCard
      layout={layout || "ebook"}
      image={card.thumbnailImage}
      title={card?.topicName || card?.title}
      subtitle={dayjs(card.updatedAt).format("D MMM, YYYY")}
      onClick={() => onItemClick(card, index)}
      className={isSelected ? "is-active" : ""}
    />
  );

  // Main visibility:
  // - detail mode: always show main
  // - list mode desktop/tablet: show main (with placeholder until selected)
  // - list mode mobile: show main only after user selects (mobileShowDetail)
  const showMain = isDetailMode || !isMobile || (isMobile && mobileShowDetail);
  
  const getTopicEntity = () => {
    const entity = getEntity(process.env.REACT_APP_ENTITY_DETAILS);
    if (entity?.id) {
      return false;
    }
    return true;
  }

  return (
    <Box
      className={`mds-container ${className} ${
        showSidebar ? "" : "no-sidebar"
      }`}
    >
      {/* Sidebar (hidden in detail mode) */}
      {showSidebar && (
        <Box className="mds-sidebar">
          <Box
            className={`mds-sidebar-list ${
              contentType === "ebook" ? "ebook-grid" : ""
            }`}
            ref={sidebarListRef}
          >
            {items.map((item, idx) => {
              const isActive = mapGetId(item) === mapGetId(selectedItem || {});
              const key = mapGetId(item) ?? `${idx}`;
              const isEbook = contentType === "ebook";
              const isChapter = contentType === "chapter";

              // Defaults: play button for media; disabled for ebook-like cards
              const hasPlay = onPlayClick ? true : true; // default on
              const hasLearn = Boolean(onLearnClick);
              const hasQuiz = Boolean(onQuizClick);

              return (
                <Box
                  key={key}
                  className={`mds-sidebar-item ${isActive ? "is-active" : ""}`}
                >
                  <SidebarListItem
                    variant={
                      isEbook ? "ebook" : isChapter ? "chapter" : "default"
                    }
                    image={mapGetThumbnail(item)}
                    title={mapGetTitle(item)}
                    subtitle={mapGetSubtitle(item)}
                    active={isActive}
                    showPlayButton={
                      !isEbook && hasPlay && !(hasLearn || hasQuiz)
                    }
                    showLearnButton={!isEbook && hasLearn}
                    showQuizButton={!isEbook && hasQuiz}
                    onClick={() => {
                      handleItemClick(item);
                    }}
                    onPlayClick={() => {
                      handleItemClick(item);
                      onPlayClick?.(item);
                    }}
                    onLearnClick={() => handleItemClick(item)}
                    onQuizClick={() => handleGoToQuiz(item)}
                  />
                </Box>
              );
            })}

            <div ref={sentinelRef} />
            {loading && (
              <Box className="mds-sidebar-loading">
                Loading...
                <CircularProgress
                  style={{ color: AppTheme.PRIMARY_COLOR }}
                  size={30}
                />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <div className="d-flex align-items-center justify-content-center">
                <Typography variant="subtitle1" color="text.secondary">
                  No items found.
                </Typography>
              </div>
            )}
          </Box>
        </Box>
      )}

      {/* Main content */}
      {showMain && (
        <Box className="mds-main" ref={mainRef}>
          {currentDetail && (
            <Box className="mds-main-header">
              <Typography className="mds-main-title">
                {currentDetail ? mapGetTitle(currentDetail) : ""}
              </Typography>

              <Box className="mds-main-actions">
                <IconButton
                  aria-label={isContentFavorited(currentDetail) ? "Remove from favorites" : "Add to favorites"}
                  onClick={() => {
                    if (currentDetail) {
                      handleFavoriteToggle(currentDetail);
                    }
                  }}
                  disabled={
                    isFavoriting || !getCurrentUserId()
                  }
                  style={{
                    color: isContentFavorited(currentDetail)
                      ? "#FF1717"
                      : "inherit",
                    opacity: isFavoriting ? 0.7 : 1,
                  }}
                >
                  {isContentFavorited(currentDetail) ? (
                    <Favorite />
                  ) : (
                    <FavoriteBorder />
                  )}
                </IconButton>
                <IconButton aria-label="share" onClick={handleShare}>
                  <Icons.OpenInNewIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {isDetailMode && loading ? (
            <Box className="mds-sidebar-loading">
              Loading...
              <CircularProgress
                style={{ color: AppTheme.PRIMARY_COLOR }}
                size={30}
              />
            </Box>
          ) : currentDetail ? (
            <>
              {(selectedChapter || contentType === "chapter") && (
                <>
                  {/* Chapter hero using existing iframe renderer (fallback to thumbnail) */}
                  {mapGetThumbnail(currentDetail) ? (
                    <IframeRenderer
                      url={""}
                      fallbackImage={mapGetThumbnail(currentDetail)}
                      thumbImages={[mapGetThumbnail(currentDetail)]}
                      className="mb-2"
                    />
                  ) : null}
                  <Box className="mds-description">
                    <Typography className="mds-description-text">
                      {displayDescription}
                      {!descExpanded && isLongDescription && (
                        <span
                          className="mds-read-more"
                          onClick={() => setDescExpanded(true)}
                        >
                          {" "}
                          Read more
                        </span>
                      )}
                    </Typography>
                  </Box>

                  {/* Toolbar: Back + Media/Read tabs */}
                  {chapterSources?.length > 0 && (
                    <Box
                      className="d-flex mb-2 align-items-center"
                      style={{ gap: 8, flexWrap: "wrap" }}
                    >
                      {contentType !== "chapter" && (
                        <MUIButton
                          primary
                          variant={"outlined"}
                          size={isMobile ? "small" : "medium"}
                          startIcon={<Icons.KeyboardBackspaceIcon />}
                          onClick={handleBackToTopic}
                          children={"Back"}
                        />
                      )}
                      {chapterSources.map((src) => (
                        <MUIButton
                          key={src.type || src.srcLink}
                          size={isMobile ? "small" : "medium"}
                          primary
                          variant={
                            (src.type || "media").toLowerCase() ===
                            (activeChapterType || "media").toLowerCase()
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() =>
                            setActiveChapterType(
                              (src.type || "media").toLowerCase()
                            )
                          }
                          children={
                            src.type.toLowerCase() === "comic"
                              ? "Comic"
                              : "Media"
                          }
                        />
                      ))}
                      <div style={{ flex: 1 }} />
                      <MUIButton
                        primary
                        size={isMobile ? "small" : "medium"}
                        onClick={() => {
                          const entity = selectedChapter || selectedItem;
                          onQuizClick?.(entity);
                          handleGoToQuiz(entity);
                        }}
                        children={"Take the Quiz"}
                      />
                    </Box>
                  )}
                </>
              )}

              <IframeRenderer
                url={
                  effectiveChapter
                    ? activeChapterUrl
                    : mapGetIframeUrl(selectedItem)
                }
                fallbackImage={
                  currentDetail ? mapGetThumbnail(currentDetail) : ""
                }
                thumbImages={
                  currentDetail ? [mapGetThumbnail(currentDetail)] : []
                }
              />
            </>
          ) : (
            !isMobile &&
            !isDetailMode && (
              <Box className="mds-iframe-empty">
                <Typography variant="subtitle1" color="text.secondary">
                  Click on an item for more information.
                </Typography>
              </Box>
            )
          )}
          {!selectedChapter && currentDetail && contentType !== "chapter" && (
            <Box className="mds-description">
              <Typography className="mds-description-text">
                {displayDescription}
                {!descExpanded && isLongDescription && (
                  <span
                    className="mds-read-more"
                    onClick={() => setDescExpanded(true)}
                  >
                    {" "}
                    Read more
                  </span>
                )}
              </Typography>

              {galleryImages.length > 0 && (
                <Box className="mds-gallery">
                  {galleryImages.map((src, idx) => (
                    <img
                      key={`${src}-${idx}`}
                      src={src}
                      alt="gallery"
                      className="mds-gallery-thumb"
                      onClick={() => {
                        setLightboxIndex(idx);
                        setLightboxSrc(src);
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {contentType === "ebook" &&
            selectedItem &&
            selectedItem?.relatedEbooks?.length > 0 &&
            typeof selectedItem.relatedEbooks[0] === "object" && (
              <div className="mb-4 e-book-grid">
                <CardGrid
                  data={selectedItem?.relatedEbooks}
                  renderItem={(card, index, onItemClick) =>
                    renderContentCard(card, index, onItemClick, "ebook")
                  }
                  type={"ebook"}
                  onItemClick={(item, index) => {
                    handleRelatedEbookClick(item);
                  }}
                  enableHorizontalScroll={true}
                  showHeader={true}
                  headerTitle={"E-Books"}
                  showSeeAll={false}
                />
              </div>
            )}

          {contentType === "topic" && selectedItem && !selectedChapter && (
            <div className="mb-4">
              {chapters.length > 0 && (
                <CardGrid
                  data={chapters}
                  showHeader={true}
                  headerTitle={"Chapters"}
                  size={{ xs: 12, sm: 6, md: 6, lg: 6 }}
                  showSeeAll={getTopicEntity()}
                  onSeeAllClick={() => {
                    const topicId = mapGetId(selectedItem);
                    const payload = {
                      topicId,
                      topicTitle: mapGetTitle(selectedItem),
                    };
                    handleSeeAllChapters(payload);
                  }}
                  renderItem={(card, index, onItemClick) => (
                    <SidebarListItem
                      variant={"chapter"}
                      image={mapGetThumbnail(card)}
                      title={mapGetTitle(card)}
                      subtitle={mapGetSubtitle(card)}
                      showPlayButton={false}
                      showLearnButton={true}
                      showQuizButton={true}
                      onLearnClick={() => setSelectedChapter(card)}
                      onQuizClick={() => handleGoToQuiz(card)}
                      onClick={() => setSelectedChapter(card)}
                    />
                  )}
                />
              )}
            </div>
          )}
          {chaptersLoading &&
            contentType === "topic" &&
            selectedItem &&
            !selectedChapter && (
              <Box className="d-flex align-items-center justify-content-center">
                Loading...
                <CircularProgress
                  style={{ color: AppTheme.PRIMARY_COLOR }}
                  size={24}
                />
              </Box>
            )}

          <Dialog
            open={Boolean(lightboxSrc)}
            onClose={() => {
              setLightboxSrc(null);
              setLightboxIndex(null);
            }}
            maxWidth="lg"
            PaperProps={{
              style: {
                boxShadow: "none",
                overflowX: "hidden",
                borderRadius: "12px",
              },
            }}
          >
            {lightboxSrc && (
              <Box className="mds-lightbox-container">
                {/* Close button */}
                <IconButton
                  aria-label="Close"
                  className="mds-lightbox-btn mds-lightbox-close"
                  onClick={() => {
                    setLightboxSrc(null);
                    setLightboxIndex(null);
                  }}
                >
                  <Close />
                </IconButton>

                {/* Previous */}
                {galleryImages.length > 1 && (
                  <IconButton
                    aria-label="Previous"
                    className="mds-lightbox-btn mds-lightbox-prev"
                    onClick={() => {
                      const total = galleryImages.length;
                      const nextIdx =
                        ((lightboxIndex ?? 0) - 1 + total) % total;
                      setLightboxIndex(nextIdx);
                      setLightboxSrc(galleryImages[nextIdx]);
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                )}

                {/* Image */}
                <img
                  src={lightboxSrc}
                  alt="full"
                  className="mds-lightbox-img"
                />

                {/* Next */}
                {galleryImages.length > 1 && (
                  <IconButton
                    aria-label="Next"
                    className="mds-lightbox-btn mds-lightbox-next"
                    onClick={() => {
                      const total = galleryImages.length;
                      const nextIdx = ((lightboxIndex ?? 0) + 1) % total;
                      setLightboxIndex(nextIdx);
                      setLightboxSrc(galleryImages[nextIdx]);
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                )}
              </Box>
            )}
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default MediaDetailWithSidebar;
